import join from "url-join"
import { isBoolean, isPresent } from "@/lib/utils"

export const PROVIDERS = {
  YOUTUBE: "youtube",
  VIMEO: "vimeo",
  FACEBOOK: "facebook",
  MUX: "mux"
} as const

export type Provider = typeof PROVIDERS[keyof typeof PROVIDERS]

export const isVimeo = (provider: Provider): boolean =>
  provider === PROVIDERS.VIMEO
export const isYoutube = (provider: Provider): boolean =>
  provider === PROVIDERS.YOUTUBE
export const isFacebook = (provider: Provider): boolean =>
  provider === PROVIDERS.FACEBOOK
export const isMux = (provider: Provider): boolean => provider === PROVIDERS.MUX

interface VideoOptions {
  loop?: boolean
  autoplay?: boolean
  hideControls?: boolean
  muted?: boolean
  playsInline?: boolean
}

const createQueryString = (params: Record<string, string>): string => {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join("&")
}

const getPlaysInlineValue = (
  playsInline: boolean | null | undefined
): string => {
  // Ensure defaults to true when there is nothing given
  return isBoolean(playsInline) && playsInline === false ? "0" : "1"
}

const getYoutubeParams = (options: VideoOptions): string => {
  return createQueryString({
    modestbranding: "1",
    autohide: "1",
    showinfo: "0",
    rel: "0",
    iv_load_policy: "3",
    enablejsapi: "1",
    fs: "1",

    autoplay: options.autoplay ? "1" : "0",
    playsinline: getPlaysInlineValue(options.playsInline),
    controls: options.hideControls ? "0" : "1",
    loop: options.loop ? "1" : "0",
    mute: options.muted ? "1" : "0"
  })
}

const getVimeoParams = (options: VideoOptions): string => {
  return createQueryString({
    byline: "false",
    portrait: "false",
    title: "false",
    speed: "true",
    transparent: "0",
    gesture: "media",

    loop: options.loop ? "1" : "0",
    muted: options.muted ? "1" : "0",
    controls: options.hideControls ? "0" : "1",
    autoplay: options.autoplay ? "1" : "0",

    playsinline: getPlaysInlineValue(options.playsInline)
  })
}

export const appendEmbedParams = (
  provider: Provider,
  link: string,
  options: VideoOptions = {}
): string => {
  const queryParams =
    provider === PROVIDERS.YOUTUBE
      ? getYoutubeParams(options)
      : provider === PROVIDERS.VIMEO
      ? getVimeoParams(options)
      : ""

  return queryParams ? join(link, `?${queryParams}`) : link
}

export const initializeFacebook = (callback: () => void): void => {
  const initFbObject = (): void => {
    window.FB.init({
      appId: process.env.VUE_APP_FACEBOOK_APP_ID || "",
      xfbml: true,
      version: "v3.2"
    })
  }

  // https://developers.facebook.com/docs/plugins/embedded-video-player/#wordpress
  let js = document.getElementsByTagName("script")[0]
  const fjs = document.getElementsByTagName("script")[0]

  if (document.getElementById("facebook-jssdk")) {
    initFbObject()
    callback()

    return
  }

  js = document.createElement("script")
  js.id = "facebook-jssdk"
  js.onload = () => {
    setTimeout(() => {
      initFbObject()
      callback()
    }, 250)
  }

  js.src = "https://connect.facebook.net/en_US/sdk.js"
  fjs.parentNode?.insertBefore(js, fjs)
}

export const generateMuxThumbnailUrl = (embedLink: string): string => {
  const playbackId = embedLink.split(".com/")[1].split(".")[0]

  return `https://image.mux.com/${playbackId}/thumbnail.png`
}

interface ThumbnailVideo {
  provider?: Provider
  embed_link?: string
  thumbnail_url?: string
  thumbnail_sizes?: Record<string, string>
}

interface ThumbnailImage {
  image: { url: string; sizes?: Record<string, string> }
}

// A video's poster resolved as an image object: an explicit image wins,
// then the stored thumbnail, then (mux only) a generated thumbnail URL.
export const videoThumbnailImage = (
  video: ThumbnailVideo | null,
  image: ThumbnailImage | null = null
): ThumbnailImage | null => {
  if (isPresent(image)) return image

  if (video?.thumbnail_url) {
    return {
      image: { url: video.thumbnail_url, sizes: video.thumbnail_sizes }
    }
  }

  if (video?.provider && isMux(video.provider) && video.embed_link) {
    return { image: { url: generateMuxThumbnailUrl(video.embed_link) } }
  }

  return null
}

// Add type declaration for the Facebook SDK
declare global {
  interface Window {
    FB: {
      init: (config: { appId: string; xfbml: boolean; version: string }) => void
    }
  }
}
